import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { listAuditLog } from "@/lib/admin-audit.functions";

export const Route = createFileRoute("/_authenticated/admin/audit")({
  head: () => ({
    meta: [
      { title: "Audit log — Admin" },
      { name: "description", content: "Immutable log of admin actions across creators, commissions and users." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuditLogPage,
});

type Entry = {
  id: string;
  admin_id: string;
  admin_email: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

const TARGET_FILTERS = ["all", "creator", "commission", "user"] as const;

function AuditLogPage() {
  const [target, setTarget] = useState<(typeof TARGET_FILTERS)[number]>("all");
  const fetchLog = useServerFn(listAuditLog);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-audit-log", target],
    queryFn: async () => {
      const res = await fetchLog({
        data: { limit: 200, targetType: target === "all" ? undefined : target },
      });
      return res.entries as Entry[];
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl">Audit log</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Every admin approval, rejection, role change, and commission status update.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TARGET_FILTERS.map((t) => (
          <button
            key={t}
            onClick={() => setTarget(t)}
            className={`-mb-px border-b-2 px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
              target === t
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

      {data && data.length === 0 && (
        <p className="text-sm text-muted-foreground">No entries yet.</p>
      )}

      {data && data.length > 0 && (
        <div className="overflow-x-auto border border-border -mx-4 sm:mx-0">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {data.map((e) => (
                <tr key={e.id} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="truncate">{e.admin_email ?? e.admin_id.slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{e.action}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      {e.target_type}
                    </div>
                    <div className="font-mono text-xs">{e.target_id?.slice(0, 8) ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <pre className="max-w-md whitespace-pre-wrap break-words text-xs text-muted-foreground">
                      {JSON.stringify(e.metadata, null, 0)}
                    </pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
